import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ArrowUpRight,
  BookOpen,
  Briefcase,
  Rocket,
  Users,
  PlayCircle,
  Sparkles,
  Compass,
  GraduationCap,
  Target,
  Award,
  MapPin,
  CheckCircle2,
  ChevronDown,
  LogIn,
  LogOut,
  Settings,
  LayoutDashboard,
  User,
  Menu,
  X,
  Pencil,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useSiteConfig } from '@/contexts/SiteConfigContext';
//import { useCurrency } from '@/contexts/CurrencyContext';
import { PublicAssetsSlideOver } from '@/components/admin/PublicAssetsSlideOver';
import defaultLogo from './homelandlogo-full.png';

// Kenya + African imagery (Unsplash — free to use)
const IMG = {
  nairobiSunny:
    'https://images.unsplash.com/photo-1741991110666-88115e724741?auto=format&fit=crop&w=1600&q=80',
  nairobiNight:
    'https://images.unsplash.com/photo-1646066394887-c6cfe3aa8887?auto=format&fit=crop&w=1600&q=80',
  nairobiSkyline:
    'https://images.unsplash.com/photo-1611348524140-53c9a25263d6?auto=format&fit=crop&w=1600&q=80',
  mountKenya:
    'https://images.unsplash.com/photo-1646159755791-54e741749028?auto=format&fit=crop&w=1600&q=80',
  savanna:
    'https://images.unsplash.com/photo-1745526180300-443ef46e6a73?auto=format&fit=crop&w=1600&q=80',
  coast:
    'https://images.unsplash.com/photo-1651860282131-e3257674ccd1?auto=format&fit=crop&w=1600&q=80',
  builder:
    'https://images.unsplash.com/photo-1531483245484-5ca8c23a880b?auto=format&fit=crop&w=1400&q=80',
  diani:
    'https://images.pexels.com/photos/3182812/pexels-photo-3182812.jpeg?auto=compress&cs=tinysrgb&w=1400',
};

const HERO_VIDEO =
  'https://videos.pexels.com/video-files/12659644/12659644-hd_1920_1080_30fps.mp4';

export function Home() {
  const { user, loading, login, logout } = useAuth();
  const { config: siteConfig, refetch: refetchSiteConfig } = useSiteConfig();
  const [publicAssetsOpen, setPublicAssetsOpen] = useState(false);
  const isAdmin = user?.role === 'admin';

  // Use heroVideoUrl from site config if available
  const heroVideoUrl = (siteConfig as any)?.heroVideoUrl?.trim() || HERO_VIDEO;

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      {/* Admin: Public Assets SlideOver for hero video editing */}
      <PublicAssetsSlideOver
        open={publicAssetsOpen}
        onClose={() => setPublicAssetsOpen(false)}
        onSaved={() => refetchSiteConfig()}
      />

      <TopNav
        user={user}
        loading={loading}
        login={login}
        logout={logout}
        onEditSite={() => setPublicAssetsOpen(true)}
      />
      <Hero heroVideoUrl={heroVideoUrl} isAdmin={isAdmin} onEditSite={() => setPublicAssetsOpen(true)} />
      <ColorBar />
      <ModulesOverview />
      <LmsSection />
      <CareersSection />
      <IncubationSection />
      <CommunitySection />
      <RootedInKenya />
      <StatsStrip />
      <FinalCta user={user} login={login} />
      <HomeFooter />
    </div>
  );
}

/* ─────────────────────────────────────────
   Top Navigation
───────────────────────────────────────── */

interface TopNavProps {
  user: any;
  loading: boolean;
  login: () => void;
  logout: () => void;
  onEditSite: () => void;
}

function TopNav({ user, loading, login, logout, onEditSite }: TopNavProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const avatarRef = useRef<HTMLDivElement>(null);
  const { config: siteConfig } = useSiteConfig();

  useEffect(() => {
    if (!avatarOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      if (avatarRef.current && !avatarRef.current.contains(e.target as Node))
        setAvatarOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [avatarOpen]);

  const normalizeLogoDimension = (value?: string | null, fallback = 'auto') => {
    const trimmed = value?.trim();
    if (!trimmed || trimmed === 'auto') return fallback;
    return /^\d+(\.\d+)?$/.test(trimmed) ? `${trimmed}px` : trimmed;
  };

  const logoUrl = (siteConfig as any)?.headerLogoUrl?.trim() || defaultLogo;
  const logoWidth = normalizeLogoDimension((siteConfig as any)?.headerLogoWidth?.trim(), 'auto');
  const logoHeight = normalizeLogoDimension((siteConfig as any)?.headerLogoHeight?.trim(), '40px');

  return (
    <header className="fixed top-0 inset-x-0 z-50">
      <div className="mx-auto max-w-7xl px-4 md:px-8 pt-4">
        <nav className="flex items-center justify-between rounded-2xl border border-white/10 bg-navy-deep/70 backdrop-blur-xl px-4 md:px-6 py-3 shadow-lg shadow-black/20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 text-white">
            <img
              src={logoUrl}
              alt="Homeland logo"
              style={{
                width: logoWidth,
                height: logoHeight,
              }}
              className="object-contain"
            />
          </Link>

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-7 text-sm text-white/80">
            <a href="#modules" className="hover:text-white transition-colors">Modules</a>
            <Link to="/courses" className="hover:text-white transition-colors">Academy</Link>
            <Link to="/careers" className="hover:text-white transition-colors">Careers</Link>
            <a href="#incubation" className="hover:text-white transition-colors">Incubation</a>
            <a href="#kenya" className="hover:text-white transition-colors">Kenya</a>
          </div>

          {/* Desktop auth */}
          <div className="hidden md:flex items-center gap-2">
            {!loading && (
              <>
                {user ? (
                  <div className="relative" ref={avatarRef}>
                    <button
                      type="button"
                      onClick={() => setAvatarOpen((o) => !o)}
                      className="flex items-center gap-2 p-1.5 rounded-full text-white hover:bg-white/10 transition-colors"
                      aria-expanded={avatarOpen}
                    >
                      {user.picture_url ? (
                        <img src={user.picture_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                      ) : (
                        <span className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-sm font-semibold text-white">
                          {user.email?.[0]?.toUpperCase() ?? user.name?.[0]?.toUpperCase() ?? '?'}
                        </span>
                      )}
                      <ChevronDown className={`w-4 h-4 transition-transform ${avatarOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {avatarOpen && (
                      <div className="absolute right-0 top-full mt-2 z-50 min-w-[220px] rounded-xl border border-border bg-card shadow-xl py-1 text-foreground">
                        <div className="px-4 py-2 border-b border-border">
                          <p className="text-sm font-medium truncate">{user.email}</p>
                          <span className="inline-block mt-1 text-xs font-medium px-2 py-0.5 rounded bg-primary/10 text-primary capitalize">
                            {user.role}
                          </span>
                        </div>
                        {user.role === 'admin' && (
                          <>
                            <Link to="/admin" className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-muted" onClick={() => setAvatarOpen(false)}>
                              <User className="w-4 h-4" /> Admin
                            </Link>
                            <button type="button" className="flex w-full items-center gap-2 px-4 py-2.5 text-sm hover:bg-muted text-left" onClick={() => { setAvatarOpen(false); onEditSite(); }}>
                              <Settings className="w-4 h-4" /> Edit public site
                            </button>
                          </>
                        )}
                        {user.role === 'tutor' && (
                          <Link to="/tutor/dashboard" className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-muted" onClick={() => setAvatarOpen(false)}>
                            <LayoutDashboard className="w-4 h-4" /> Dashboard
                          </Link>
                        )}
                        {user.role === 'student' && (
                          <Link to="/dashboard" className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-muted" onClick={() => setAvatarOpen(false)}>
                            <LayoutDashboard className="w-4 h-4" /> Dashboard
                          </Link>
                        )}
                        <Link to="/courses" className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-muted" onClick={() => setAvatarOpen(false)}>
                          Courses
                        </Link>
                        <button type="button" className="flex w-full items-center gap-2 px-4 py-2.5 text-sm hover:bg-muted text-left" onClick={() => { setAvatarOpen(false); logout(); }}>
                          <LogOut className="w-4 h-4" /> Log out
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={login}
                      className="text-sm text-white/80 hover:text-white px-3 py-1.5 transition-colors"
                    >
                      Sign in
                    </button>
                    <Link
                      to="/courses"
                      className="inline-flex items-center gap-1.5 rounded-xl bg-gold text-navy-deep hover:bg-gold-light transition-colors px-4 py-2 text-sm font-semibold"
                    >
                      Join Homeland <ArrowRight className="w-4 h-4" />
                    </Link>
                  </>
                )}
              </>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            type="button"
            className="md:hidden p-2 text-white"
            onClick={() => setMobileOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </nav>

        {/* Mobile dropdown */}
        {mobileOpen && (
          <div className="mt-2 mx-0 rounded-2xl border border-white/10 bg-navy-deep/90 backdrop-blur-xl px-4 py-4 shadow-xl">
            <nav className="flex flex-col gap-1 text-white/80 text-sm mb-4">
              <a href="#modules" className="py-2.5 px-3 rounded-lg hover:bg-white/10 hover:text-white" onClick={() => setMobileOpen(false)}>Modules</a>
              <Link to="/courses" className="py-2.5 px-3 rounded-lg hover:bg-white/10 hover:text-white" onClick={() => setMobileOpen(false)}>Academy</Link>
              <Link to="/careers" className="py-2.5 px-3 rounded-lg hover:bg-white/10 hover:text-white" onClick={() => setMobileOpen(false)}>Careers</Link>
              <a href="#incubation" className="py-2.5 px-3 rounded-lg hover:bg-white/10 hover:text-white" onClick={() => setMobileOpen(false)}>Incubation</a>
              <Link to="/about" className="py-2.5 px-3 rounded-lg hover:bg-white/10 hover:text-white" onClick={() => setMobileOpen(false)}>About</Link>
              <Link to="/ecosystem" className="py-2.5 px-3 rounded-lg hover:bg-white/10 hover:text-white" onClick={() => setMobileOpen(false)}>Ecosystem</Link>
            </nav>
            {!loading && (
              <div className="border-t border-white/10 pt-3">
                {user ? (
                  <div className="flex flex-col gap-1 text-sm text-white/80">
                    <p className="px-3 py-1 text-xs text-white/40 truncate">{user.email}</p>
                    {user.role === 'admin' && (
                      <Link to="/admin" className="py-2.5 px-3 rounded-lg hover:bg-white/10 hover:text-white flex items-center gap-2" onClick={() => setMobileOpen(false)}>
                        <User className="w-4 h-4" /> Admin
                      </Link>
                    )}
                    {user.role === 'tutor' && (
                      <Link to="/tutor/dashboard" className="py-2.5 px-3 rounded-lg hover:bg-white/10 hover:text-white flex items-center gap-2" onClick={() => setMobileOpen(false)}>
                        <LayoutDashboard className="w-4 h-4" /> Dashboard
                      </Link>
                    )}
                    {user.role === 'student' && (
                      <Link to="/dashboard" className="py-2.5 px-3 rounded-lg hover:bg-white/10 hover:text-white flex items-center gap-2" onClick={() => setMobileOpen(false)}>
                        <LayoutDashboard className="w-4 h-4" /> Dashboard
                      </Link>
                    )}
                    <button type="button" className="py-2.5 px-3 rounded-lg hover:bg-white/10 hover:text-white flex items-center gap-2 text-left w-full" onClick={() => { setMobileOpen(false); logout(); }}>
                      <LogOut className="w-4 h-4" /> Log out
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    <button type="button" onClick={() => { setMobileOpen(false); login(); }} className="flex items-center gap-2 py-2.5 px-3 rounded-lg text-sm text-white/80 hover:bg-white/10 hover:text-white">
                      <LogIn className="w-4 h-4" /> Sign in
                    </button>
                    <Link to="/courses" className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-gold text-navy-deep px-4 py-3 text-sm font-semibold" onClick={() => setMobileOpen(false)}>
                      Join Homeland <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}

/* ─────────────────────────────────────────
   Hero Section
───────────────────────────────────────── */

function Hero({ heroVideoUrl, isAdmin, onEditSite }: { heroVideoUrl: string; isAdmin: boolean; onEditSite: () => void }) {
  return (
    <section className="relative min-h-[100svh] flex items-center overflow-hidden bg-navy-deep">
      {/* Video background */}
      <video
        className="absolute inset-0 w-full h-full object-cover opacity-55"
        autoPlay
        muted
        loop
        playsInline
        poster={IMG.nairobiNight}
      >
        <source src={heroVideoUrl} type="video/mp4" />
      </video>

      {/* Overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-navy-deep/60 via-navy-deep/40 to-navy-deep" />
      <div className="absolute inset-0 bg-gradient-to-r from-navy-deep/85 via-navy-deep/30 to-transparent" />
      <div className="pointer-events-none absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-gold/20 blur-[120px]" />
      <div className="pointer-events-none absolute bottom-0 left-1/4 w-[400px] h-[400px] rounded-full bg-africa-orange/20 blur-[120px]" />

      <div className="relative z-10 max-w-7xl mx-auto w-full px-6 md:px-8 pt-32 pb-24">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 backdrop-blur-md text-xs font-medium text-white/90 mb-6">
            <span className="flex h-1.5 w-1.5 rounded-full bg-africa-green animate-pulse-dot" />
            <MapPin className="w-3.5 h-3.5 text-gold" />
            Developed by africans · for africans
          </div>

          <h1 className="font-display font-black text-white text-5xl md:text-7xl lg:text-[5.5rem] leading-[0.95] tracking-tight">
            Africa's home for
            <br />
            <span className="text-gradient-gold">bold ideas.</span>
          </h1>

          <p className="mt-6 max-w-xl text-lg md:text-xl text-white/75 leading-relaxed">
            Homeland is a tech hub for the next generation of african builders — a
            learning academy, careers portal, and incubation studio under one roof.
            Learn a skill, land a job, launch a company.
          </p>

          <div className="mt-9 flex flex-wrap gap-3">
            <a
              href="#modules"
              className="inline-flex items-center gap-2 rounded-xl bg-gold hover:bg-gold-light text-navy-deep px-6 py-3.5 text-base font-semibold shadow-xl shadow-gold/20 transition-colors"
            >
              Explore the hub <ArrowRight className="w-5 h-5" />
            </a>
            <a
              href="#lms"
              className="inline-flex items-center gap-2 rounded-xl border border-white/25 bg-white/5 hover:bg-white/10 backdrop-blur text-white px-6 py-3.5 text-base font-semibold transition-colors"
            >
              <PlayCircle className="w-5 h-5" /> See how it works
            </a>
          </div>

          <div className="mt-12 grid grid-cols-3 gap-6 max-w-lg">
            <HeroStat value="4" label="Pillars" />
            <HeroStat value="20+" label="Founders backed" />
            <HeroStat value="11" label="African Cities" />
          </div>
        </div>
      </div>

      {/* Admin edit button */}
      {isAdmin && (
        <button
          type="button"
          onClick={onEditSite}
          className="absolute top-24 right-6 z-20 flex items-center gap-1.5 rounded-lg bg-background/80 border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-background transition-colors"
        >
          <Pencil className="w-3.5 h-3.5" /> Edit hero
        </button>
      )}

      {/* Scroll cue */}
      <a
        href="#modules"
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/60 hover:text-white text-xs tracking-[0.3em] uppercase animate-float-slow"
      >
        Scroll ↓
      </a>
    </section>
  );
}

function HeroStat({ value, label }: { value: string; label: string }) {
  return (
    <div>
      <div className="font-display text-3xl md:text-4xl font-bold text-white">{value}</div>
      <div className="text-[11px] uppercase tracking-[0.2em] text-white/60 mt-1">{label}</div>
    </div>
  );
}

/* ─────────────────────────────────────────
   Color Bar
───────────────────────────────────────── */

function ColorBar() {
  const colors = [
    'bg-africa-red',
    'bg-africa-orange',
    'bg-africa-yellow',
    'bg-africa-green',
    'bg-africa-blue',
    'bg-africa-purple',
  ];
  return (
    <div className="flex h-1.5 w-full">
      {colors.map((c) => (
        <div key={c} className={`flex-1 ${c}`} />
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────
   Modules Overview
───────────────────────────────────────── */

const MODULES = [
  {
    id: 'lms',
    icon: GraduationCap,
    tag: 'Learn',
    title: 'Homeland Academy',
    desc: 'An LMS built for African learners — live cohorts, self-paced tracks, and mentorship in AI, product, design, and business.',
    accent: 'africa-blue',
    href: '/courses',
    isRoute: true,
  },
  {
    id: 'careers',
    icon: Briefcase,
    tag: 'Work',
    title: 'Careers Portal',
    desc: 'A curated pipeline from african talent to hiring startups. Verified profiles, referrals, and paid opportunities.',
    accent: 'africa-green',
    href: '/careers',
    isRoute: true,
  },
  {
    id: 'incubation',
    icon: Rocket,
    tag: 'Build',
    title: 'Incubation Programs',
    desc: 'Founders Lab to Scale Accelerator — from your first prototype to Series A, with capital, mentors, and studio space.',
    accent: 'africa-orange',
    href: '/ecosystem',
    isRoute: true,
  },
  {
    id: 'community',
    icon: Users,
    tag: 'Belong',
    title: 'The Community',
    desc: 'Meetups, hackathons, referrals and a network of 3,000+ operators from Nairobi, kampala, addis ababa, dar es salaam and beyond.',
    accent: 'africa-purple',
    href: '/about',
    isRoute: true,
  },
] as const;

function ModulesOverview() {
  return (
    <section id="modules" className="relative py-24 md:py-32 px-6 md:px-8 bg-background bg-grain">
      <div className="max-w-7xl mx-auto">
        <SectionEyebrow>The Hub, in four modules</SectionEyebrow>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mt-3 mb-14">
          <h2 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl text-foreground max-w-2xl leading-[1.05]">
            One roof for the full innovator journey.
          </h2>
          <p className="text-muted-foreground max-w-md text-base leading-relaxed">
            Whether you're a student learning to code, a graduate hunting your first
            role, or a founder scaling a startup — Homeland has a home for you.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {MODULES.map((m, i) => (
            <Link
              key={m.id}
              to={m.href}
              className="group relative overflow-hidden rounded-3xl border border-border bg-card p-8 md:p-10 hover:border-foreground/30 transition-all hover:-translate-y-1 hover:shadow-2xl hover:shadow-black/5"
            >
              <div className="flex items-start justify-between gap-6">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-6">
                    <span className={`inline-flex h-11 w-11 items-center justify-center rounded-xl bg-${m.accent}/10 text-${m.accent} border border-${m.accent}/20`}>
                      <m.icon className="w-5 h-5" />
                    </span>
                    <span className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">
                      0{i + 1} · {m.tag}
                    </span>
                  </div>
                  <h3 className="font-display font-bold text-2xl md:text-3xl text-foreground mb-3">
                    {m.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed text-[15px] max-w-md">
                    {m.desc}
                  </p>
                  <div className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-foreground group-hover:text-accent transition-colors">
                    Learn more <ArrowUpRight className="w-4 h-4" />
                  </div>
                </div>
                <div className={`hidden md:block h-24 w-24 rounded-2xl bg-${m.accent}/10 border border-${m.accent}/20 shrink-0 relative overflow-hidden`}>
                  <div className={`absolute inset-0 bg-gradient-to-br from-${m.accent}/20 to-transparent`} />
                  <m.icon className={`absolute inset-0 m-auto w-10 h-10 text-${m.accent}/60`} />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────
   LMS Deep-dive
───────────────────────────────────────── */

function LmsSection() {
  return (
    <section id="lms" className="relative py-24 md:py-32 px-6 md:px-8 bg-navy text-white overflow-hidden">
      <div className="absolute -top-40 right-0 w-[500px] h-[500px] rounded-full bg-africa-blue/25 blur-[120px]" />
      <div className="absolute bottom-0 -left-40 w-[400px] h-[400px] rounded-full bg-africa-purple/25 blur-[120px]" />

      <div className="relative max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-5 relative order-2 lg:order-1">
          <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl shadow-black/40">
            <img
              src={IMG.builder}
              alt="African developer building software at Homeland Academy"
              className="w-full h-[520px] object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-navy-deep/70 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 flex items-center gap-3 rounded-2xl bg-navy-deep/70 backdrop-blur-md border border-white/10 px-4 py-3">
              <div className="flex -space-x-2">
                {['africa-orange', 'africa-green', 'africa-blue'].map((c) => (
                  <div key={c} className={`h-8 w-8 rounded-full border-2 border-navy-deep bg-${c}`} />
                ))}
              </div>
              <div className="text-sm">
                <div className="font-semibold">Live cohort · AI Engineering</div>
                <div className="text-white/60 text-xs">42 learners · Starts Monday</div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7 order-1 lg:order-2">
          <SectionEyebrow tone="light">01 · Homeland Academy</SectionEyebrow>
          <h2 className="mt-3 font-display font-bold text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
            Learn the skills that{' '}
            <span className="text-gradient-gold">Africa's economy is hiring for.</span>
          </h2>
          <p className="mt-5 text-white/70 text-lg leading-relaxed max-w-xl">
            A full learning management system with live tutor-led cohorts, self-paced
            tracks, homework grading, and one-on-one mentorship — designed for African
            learners, in African time zones.
          </p>

          <div className="mt-10 grid sm:grid-cols-3 gap-4">
            <StepCard n="1" title="Pick a track" body="Software, AI, Product, Design, or Business — all built with Kenyan employers." />
            <StepCard n="2" title="Learn live" body="Weekly cohort sessions, homework, and mentor reviews inside the LMS." />
            <StepCard n="3" title="Get placed" body="Top learners get referred through the Careers Portal to hiring partners." />
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <Link
              to="/courses"
              className="inline-flex items-center gap-2 rounded-xl bg-gold hover:bg-gold-light text-navy-deep px-5 py-3 font-semibold transition-colors"
            >
              Browse courses <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-xl border border-white/25 hover:bg-white/10 text-white px-5 py-3 font-semibold transition-colors"
            >
              Become a tutor
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function StepCard({ n, title, body }: { n: string; title: string; body: string }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-5">
      <div className="text-gold font-display font-bold text-2xl">{n}</div>
      <div className="mt-2 font-display font-semibold text-lg">{title}</div>
      <div className="mt-1 text-sm text-white/60 leading-relaxed">{body}</div>
    </div>
  );
}

/* ─────────────────────────────────────────
   Careers Section
───────────────────────────────────────── */

function CareersSection() {
  return (
    <section id="careers" className="relative py-24 md:py-32 px-6 md:px-8">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-7">
          <SectionEyebrow>02 · Careers Portal</SectionEyebrow>
          <h2 className="mt-3 font-display font-bold text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-foreground">
            From <span className="text-africa-green">talent</span> to{' '}
            <span className="text-africa-orange">first paycheck.</span>
          </h2>
          <p className="mt-5 text-muted-foreground text-lg leading-relaxed max-w-xl">
            A jobs board that actually works for africa. Verified profiles from the
            Academy, curated roles from vetted startups, and referral rewards for the
            community. No spam. No ghosting.
          </p>

          <ul className="mt-8 space-y-3">
            {[
              'Verified skills — Academy grades and projects on every profile.',
              'Nairobi-first startups. Remote roles across East Africa and beyond.',
              'Referral bounties when your friend gets hired.',
              'Interview prep and offer-letter reviews from Homeland mentors.',
            ].map((line) => (
              <li key={line} className="flex items-start gap-3 text-foreground">
                <CheckCircle2 className="w-5 h-5 text-africa-green mt-0.5 shrink-0" />
                <span className="leading-relaxed">{line}</span>
              </li>
            ))}
          </ul>

          <div className="mt-10 flex flex-wrap gap-3">
            <Link
              to="/careers"
              className="inline-flex items-center gap-2 rounded-xl bg-primary text-primary-foreground hover:bg-navy-deep px-5 py-3 font-semibold transition-colors"
            >
              See open roles <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-xl border border-border hover:bg-muted text-foreground px-5 py-3 font-semibold transition-colors"
            >
              Post a job
            </Link>
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-africa-green/20 to-africa-orange/20 rounded-3xl blur-2xl" />
            <div className="relative rounded-3xl border border-border bg-card p-6 shadow-xl">
              <div className="flex items-center justify-between mb-5">
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">
                  Careers · Live
                </div>
                <span className="flex items-center gap-1.5 text-xs text-africa-green font-medium">
                  <span className="h-1.5 w-1.5 rounded-full bg-africa-green animate-pulse-dot" />
                  47 hiring now
                </span>
              </div>
              <div className="space-y-3">
                {[
                  { role: 'Frontend Engineer', co: 'Twiga Foods', city: 'Nairobi', tag: 'africa-blue' },
                  { role: 'AI Product Manager', co: 'M-KOPA', city: 'Nairobi · Hybrid', tag: 'africa-purple' },
                  { role: 'Growth Lead', co: 'Kwara', city: 'Remote · Kenya', tag: 'africa-green' },
                  { role: 'UI Designer', co: 'Sendy', city: 'Mombasa · Remote', tag: 'africa-orange' },
                ].map((j) => (
                  <Link
                    key={j.role}
                    to="/careers"
                    className="flex items-center justify-between gap-4 rounded-xl border border-border bg-background/60 px-4 py-3 hover:border-foreground/20 transition-colors"
                  >
                    <div className="min-w-0">
                      <div className="font-semibold text-foreground truncate">{j.role}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {j.co} · {j.city}
                      </div>
                    </div>
                    <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-md bg-${j.tag}/10 text-${j.tag}`}>
                      Apply
                    </span>
                  </Link>
                ))}
              </div>
              <Link to="/careers" className="mt-5 block text-center text-sm text-muted-foreground hover:text-foreground py-2">
                + 43 more open roles
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────
   Incubation Section
───────────────────────────────────────── */

function IncubationSection() {
  const programs = [
    {
      title: 'Founders Lab',
      stage: 'Idea · Pre-seed',
      body: '12-week program to validate, prototype and launch your first product. Weekly mentor reviews, KES 500k stipend, and demo day.',
      accent: 'africa-yellow',
    },
    {
      title: 'Scale Accelerator',
      stage: 'Seed · Series A',
      body: 'For post-revenue startups. Systemize growth, hire your first ten, and prepare an institutional round with Homeland\'s investor network.',
      accent: 'africa-orange',
    },
    {
      title: 'Studio Residency',
      stage: 'In-house builds',
      body: 'Co-build a company with Homeland\'s operator team, capital, and infrastructure. Equity partnership from day one.',
      accent: 'africa-red',
    },
  ];

  return (
    <section
      id="incubation"
      className="relative py-24 md:py-32 px-6 md:px-8 bg-navy-deep text-white overflow-hidden"
    >
      <div className="absolute inset-0 opacity-30">
        <img
          src={IMG.nairobiSkyline}
          alt="Nairobi skyline"
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-navy-deep/95 via-navy-deep/85 to-navy-deep" />
      <div className="absolute top-1/2 -right-40 w-[500px] h-[500px] rounded-full bg-africa-orange/25 blur-[130px]" />

      <div className="relative max-w-7xl mx-auto">
        <div className="max-w-3xl">
          <SectionEyebrow tone="light">03 · Incubation</SectionEyebrow>
          <h2 className="mt-3 font-display font-bold text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
            We back founders{' '}
            <span className="text-gradient-gold">from Nairobi to the world.</span>
          </h2>
          <p className="mt-5 text-white/70 text-lg leading-relaxed">
            Three programs, one bet: that the next billion-dollar company in Africa is
            being sketched on a whiteboard somewhere in Kenya right now. Homeland gives
            it capital, mentors, and studio space.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
          {programs.map((p, i) => (
            <Link
              key={p.title}
              to="/ecosystem"
              className="group relative rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-7 hover:border-gold/40 transition-all hover:-translate-y-1"
            >
              <div className="flex items-center justify-between mb-6">
                <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl bg-${p.accent}/15 border border-${p.accent}/25 text-${p.accent}`}>
                  {i === 0 ? <Compass className="w-5 h-5" /> : i === 1 ? <Target className="w-5 h-5" /> : <Sparkles className="w-5 h-5" />}
                </span>
                <span className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-semibold">
                  Stage 0{i + 1}
                </span>
              </div>
              <div className="text-xs text-gold uppercase tracking-[0.18em] mb-2 font-semibold">
                {p.stage}
              </div>
              <h3 className="font-display font-bold text-2xl mb-3">{p.title}</h3>
              <p className="text-white/70 text-sm leading-relaxed">{p.body}</p>
              <div className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-gold group-hover:gap-2.5 transition-all">
                Apply <ArrowRight className="w-4 h-4" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────
   Community Section
───────────────────────────────────────── */

function CommunitySection() {
  return (
    <section id="community" className="relative py-24 md:py-32 px-6 md:px-8">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6 order-2 lg:order-1">
          <div className="grid grid-cols-2 gap-4">
            <img src={IMG.diani} alt="Kenyan tech community meetup" className="rounded-2xl h-72 w-full object-cover" loading="lazy" />
            <img src={IMG.coast} alt="Kenyan coastline" className="rounded-2xl h-72 w-full object-cover mt-8" loading="lazy" />
            <img src={IMG.nairobiSunny} alt="Nairobi cityscape" className="rounded-2xl h-72 w-full object-cover -mt-4" loading="lazy" />
            <img src={IMG.savanna} alt="Kenyan savanna wildlife" className="rounded-2xl h-72 w-full object-cover mt-4" loading="lazy" />
          </div>
        </div>

        <div className="lg:col-span-6 order-1 lg:order-2">
          <SectionEyebrow>04 · Community</SectionEyebrow>
          <h2 className="mt-3 font-display font-bold text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-foreground">
            60+ builders, one <span className="text-africa-purple">network.</span>
          </h2>
          <p className="mt-5 text-muted-foreground text-lg leading-relaxed max-w-xl">
            Monthly meetups in Nairobi and Mombasa, quarterly hackathons, and a
            community of operators, founders, engineers, and creatives who show up for
            each other.
          </p>

          <div className="mt-8 grid grid-cols-2 gap-3 max-w-lg">
            {[
              { icon: Users, label: 'Community members', val: '140+' },
              { icon: Award, label: 'Hackathons a year', val: '2' },
              { icon: MapPin, label: 'Meetup cities', val: '5' },
              { icon: BookOpen, label: 'Referral bounties', val: 'KES 100k' },
            ].map((s) => (
              <div key={s.label} className="rounded-2xl border border-border bg-card p-4">
                <s.icon className="w-5 h-5 text-accent mb-2" />
                <div className="font-display font-bold text-xl text-foreground">{s.val}</div>
                <div className="text-xs text-muted-foreground">{s.label}</div>
              </div>
            ))}
          </div>

          <Link
            to="/contact"
            className="mt-8 inline-flex items-center gap-2 rounded-xl bg-primary text-primary-foreground hover:bg-navy-deep px-5 py-3 font-semibold transition-colors"
          >
            Join the community <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────
   Rooted in Kenya — landmark gallery
───────────────────────────────────────── */

function RootedInKenya() {
  const cards = [
    { img: IMG.nairobiNight, city: 'Eldoret', note: 'HQ · KCB building' },
    { img: IMG.mountKenya, city: 'Mount Kenya', note: 'Founders retreat' },
    { img: IMG.coast, city: 'Mombasa', note: 'Coastal hub' },
    { img: IMG.savanna, city: 'Maasai Mara', note: 'Annual offsite' },
  ];
  return (
    <section id="kenya" className="relative py-24 md:py-32 px-6 md:px-8 bg-muted/40">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <SectionEyebrow>Rooted in africa</SectionEyebrow>
            <h2 className="mt-3 font-display font-bold text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-foreground max-w-2xl">
              Built where the wildebeest run and the matatus honk.
            </h2>
          </div>
          <p className="text-muted-foreground max-w-md leading-relaxed">
            Homeland is unapologetically african. Our HQ is in Eldoret, kenya at the KCB building, our founders
            retreat is on the slopes of Mount Kenya, and our annual offsite is under
            the acacia trees of the Mara.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {cards.map((c, i) => (
            <div
              key={c.city}
              className="group relative aspect-[3/4] rounded-3xl overflow-hidden border border-border"
              style={{ transform: `translateY(${i % 2 === 0 ? 0 : 24}px)` }}
            >
              <img
                src={c.img}
                alt={c.city}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-deep via-navy-deep/20 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 text-white">
                <div className="text-[10px] uppercase tracking-[0.2em] text-gold font-semibold">
                  {c.note}
                </div>
                <div className="font-display font-bold text-2xl mt-1">{c.city}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────
   Stats Strip
───────────────────────────────────────── */

function StatsStrip() {
  //const { formatKesPrice } = useCurrency();
  //const capitalCatalysedValue = formatKesPrice(6000_000);

  const stats = [
    { v: '20+', l: 'Startups backed' },
    { v: '6M', l: 'Capital catalysed' },
    { v: '140+', l: 'Community members' },
    { v: '35+', l: 'Active mentors' },
    { v: '11', l: 'frican cities' },
  ];
  return (
    <section className="relative py-16 px-6 md:px-8 bg-primary text-primary-foreground overflow-hidden">
      <div className="absolute top-0 right-0 w-80 h-80 rounded-full bg-gold/15 blur-[100px]" />
      <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full bg-africa-green/12 blur-[100px]" />
      <div className="relative max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-5 gap-8">
        {stats.map((s) => (
          <div key={s.l} className="text-center md:text-left">
            <div className="font-display font-bold text-3xl md:text-4xl text-gold-light">
              {s.v}
            </div>
            <div className="text-xs uppercase tracking-[0.2em] text-primary-foreground/60 mt-2">
              {s.l}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────
   Final CTA
───────────────────────────────────────── */

function FinalCta({ user, login }: { user: any; login: () => void }) {
  return (
    <section id="join" className="relative py-24 md:py-32 px-6 md:px-8 overflow-hidden">
      <div className="absolute inset-0 -z-10 bg-grain" />
      <div className="max-w-4xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold text-muted-foreground mb-6">
          <Sparkles className="w-3.5 h-3.5 text-accent" />
          Applications open · Cohort 07
        </div>
        <h2 className="font-display font-black text-5xl md:text-6xl lg:text-7xl text-foreground leading-[1]">
          Come build the future{' '}
          <span className="text-gradient-gold">from home.</span>
        </h2>
        <p className="mt-6 text-muted-foreground text-lg md:text-xl max-w-xl mx-auto leading-relaxed">
          Whether you're learning, hiring, launching, or looking for your people —
          Homeland is where african tech comes together.
        </p>
        <div className="mt-10 flex flex-wrap gap-3 justify-center">
          <Link
            to="/courses"
            className="inline-flex items-center gap-2 rounded-xl bg-primary text-primary-foreground hover:bg-navy-deep px-7 py-4 text-base font-semibold shadow-xl shadow-primary/20 transition-colors"
          >
            Get started <ArrowRight className="w-5 h-5" />
          </Link>
          {user ? (
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-xl border border-border bg-card hover:bg-muted text-foreground px-7 py-4 text-base font-semibold transition-colors"
            >
              Talk to a mentor
            </Link>
          ) : (
            <button
              type="button"
              onClick={login}
              className="inline-flex items-center gap-2 rounded-xl border border-border bg-card hover:bg-muted text-foreground px-7 py-4 text-base font-semibold transition-colors"
            >
              Sign in to Homeland
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────
   Footer
───────────────────────────────────────── */

function HomeFooter() {
  return (
    <footer className="relative bg-navy-deep text-white/70 pt-16 pb-10 px-6 md:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8 pb-12 border-b border-white/10">
          <div className="col-span-2">
            <Link to="/" className="flex items-center gap-2.5 text-white mb-4">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-gold to-africa-orange text-navy-deep font-black text-sm">
                H
              </span>
              <span className="font-display font-bold text-lg">
                Homeland<span className="text-gold">.</span>
              </span>
            </Link>
            <p className="text-sm max-w-sm leading-relaxed">
              Afrca's's home for bold ideas. An LMS, careers portal, and incubation
              studio for the next generation of African builders.
            </p>
            <div className="mt-5 flex items-center gap-2 text-xs text-white/50">
              <MapPin className="w-4 h-4 text-gold" />
              KCB building, Eldoret · Kenya
            </div>
          </div>
          <FooterCol
            title="Modules"
            links={[
              { label: 'Academy', to: '/courses' },
              { label: 'Careers', to: '/careers' },
              { label: 'Incubation', to: '/ecosystem' },
              { label: 'Community', to: '/contact' },
            ]}
          />
          <FooterCol
            title="Company"
            links={[
              { label: 'About', to: '/about' },
              { label: 'Ecosystem', to: '/ecosystem' },
              { label: 'Contact', to: '/contact' },
              { label: 'Referral', to: '/referral' },
            ]}
          />
          <FooterCol
            title="Platform"
            links={[
              { label: 'Courses', to: '/courses' },
              { label: 'Login', to: '/login' },
              { label: 'Dashboard', to: '/dashboard' },
              { label: 'Contact', to: '/contact' },
            ]}
          />
        </div>
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 pt-8 text-xs text-white/40">
          <div>© {new Date().getFullYear()} Homeland Hub. Made in Nairobi with 🇰🇪.</div>
          <div className="flex gap-2">
            {['bg-africa-red', 'bg-africa-orange', 'bg-africa-yellow', 'bg-africa-green', 'bg-africa-blue', 'bg-africa-purple'].map((c) => (
              <span key={c} className={`h-1.5 w-6 rounded-full ${c}`} />
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, links }: { title: string; links: { label: string; to: string }[] }) {
  return (
    <div>
      <div className="text-white font-semibold text-sm mb-4">{title}</div>
      <ul className="space-y-2 text-sm">
        {links.map((l) => (
          <li key={l.label}>
            <Link to={l.to} className="hover:text-white transition-colors">
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ─────────────────────────────────────────
   Shared Utilities
───────────────────────────────────────── */

function SectionEyebrow({
  children,
  tone = 'dark',
}: {
  children: React.ReactNode;
  tone?: 'dark' | 'light';
}) {
  return (
    <div
      className={`inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold ${tone === 'light' ? 'text-gold' : 'text-africa-orange'
        }`}
    >
      <span className={`h-px w-8 ${tone === 'light' ? 'bg-gold/60' : 'bg-africa-orange/60'}`} />
      {children}
    </div>
  );
}
